import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy - Smartasaurus" },
      { name: "description", content: "How Smartasaurus collects, uses, and protects your data. GDPR-compliant policy for our newsletter, waitlists, analytics, and future products." },
      { property: "og:title", content: "Privacy Policy - Smartasaurus" },
      { property: "og:description", content: "How we handle your data. Clear, GDPR-compliant, no dark patterns." },
      { property: "og:url", content: "https://smartasaurus.com/privacy" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Privacy Policy - Smartasaurus" },
      { name: "twitter:description", content: "How we handle your data. Clear, GDPR-compliant, no dark patterns." },
    ],
    links: [{ rel: "canonical", href: "https://smartasaurus.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-16 md:px-8 md:py-20">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· Legal</div>
        <h1 className="mt-3 font-display text-4xl uppercase leading-[1] md:text-6xl">Privacy Policy</h1>
        <p className="mt-3 text-sm text-muted-foreground">Last updated: June 2026</p>

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-muted-foreground">
          <section>
            <h2 className="font-display text-xl uppercase text-foreground">1. Who we are</h2>
            <p className="mt-2">
              Smartasaurus is a science-media brand operated as a sole proprietorship registered in Sweden.
              For privacy matters, contact us at <a href="mailto:support@smartasaurus.com" className="text-primary underline-offset-4 hover:underline">support@smartasaurus.com</a>.
              We are the data controller for personal data described below, under the EU General Data Protection Regulation (GDPR).
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">2. What we collect</h2>
            <ul className="mt-2 list-disc space-y-2 pl-5">
              <li><span className="text-foreground">Newsletter & waitlists:</span> your email address (and optional name) when you sign up.</li>
              <li><span className="text-foreground">Account data:</span> if you create an account, your email, display name, and avatar (if provided).</li>
              <li><span className="text-foreground">Reading activity:</span> anonymous page views and on-site interactions (article views, quiz answers, saves) tied to a session ID.</li>
              <li><span className="text-foreground">Contact messages:</span> the name, email, and message you submit via our contact form.</li>
              <li><span className="text-foreground">Future purchases:</span> when we sell digital or physical products, our payment provider will process billing data on our behalf.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">3. Why we use it (legal basis)</h2>
            <ul className="mt-2 list-disc space-y-2 pl-5">
              <li><span className="text-foreground">Newsletter & waitlists:</span> consent (Art. 6(1)(a) GDPR). You can withdraw any time via the unsubscribe link.</li>
              <li><span className="text-foreground">Account & purchases:</span> performance of a contract (Art. 6(1)(b)).</li>
              <li><span className="text-foreground">Analytics & site improvements:</span> legitimate interest (Art. 6(1)(f)) in operating and improving the site, balanced against your privacy.</li>
              <li><span className="text-foreground">Legal obligations:</span> bookkeeping, tax, and consumer law where applicable (Art. 6(1)(c)).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">4. Cookies & analytics</h2>
            <p className="mt-2">
              We use privacy-friendly, cookieless analytics (Plausible) that do not track you across sites or build personal profiles.
              We use strictly-necessary local storage to remember things like your session, saved articles, and quiz progress.
              We do not use advertising cookies or third-party tracking pixels.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">5. Who we share data with</h2>
            <p className="mt-2">We use a small number of trusted processors, bound by data-processing agreements:</p>
            <ul className="mt-2 list-disc space-y-2 pl-5">
              <li>Supabase - database, authentication, and file storage.</li>
              <li>Resend - transactional and newsletter email delivery.</li>
              <li>ElevenLabs - on-demand article narration (text only, no personal data).</li>
              <li>Plausible - privacy-friendly site analytics.</li>
              <li>A payment provider (e.g. Stripe) once paid products launch.</li>
            </ul>
            <p className="mt-2">Some processors may be located outside the EU/EEA. Where this is the case, transfers rely on Standard Contractual Clauses or equivalent safeguards.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">6. How long we keep it</h2>
            <ul className="mt-2 list-disc space-y-2 pl-5">
              <li>Newsletter subscribers: until you unsubscribe.</li>
              <li>Waitlist entries: until the product launches or you ask us to delete them.</li>
              <li>Account data: until you delete your account.</li>
              <li>Contact messages: up to 24 months, then deleted.</li>
              <li>Order/billing records: as long as Swedish bookkeeping law requires (currently 7 years).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">7. Your rights under GDPR</h2>
            <p className="mt-2">You have the right to access, correct, delete, restrict, port, and object to processing of your data, and to withdraw consent at any time.</p>
            <p className="mt-2">To exercise any of these, email <a href="mailto:support@smartasaurus.com" className="text-primary underline-offset-4 hover:underline">support@smartasaurus.com</a>. We aim to respond within 30 days.</p>
            <p className="mt-2">You may also lodge a complaint with the Swedish data protection authority, <span className="text-foreground">Integritetsskyddsmyndigheten (IMY)</span>.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">8. Children</h2>
            <p className="mt-2">Smartasaurus is intended for a general adult audience. We do not knowingly collect personal data from children under 13. If you believe a child has provided us data, contact us and we will delete it.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">9. Changes</h2>
            <p className="mt-2">We may update this policy as the site evolves. Material changes will be announced via the newsletter or a notice on this page.</p>
          </section>

          <p className="pt-4">
            See also: <Link to="/terms" className="text-primary underline-offset-4 hover:underline">Terms</Link> · <Link to="/refunds" className="text-primary underline-offset-4 hover:underline">Refunds</Link> · <Link to="/contact" className="text-primary underline-offset-4 hover:underline">Contact</Link>
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
